/**
 * Android-style home icon grid.
 * Empty home is valid — apps live in Applications / Spotlight / App Manager.
 */
import { useEffect, useMemo, useState, type DragEvent } from "react";
import { osStore } from "../os/store";
import { resolveAppIcon } from "../os/icons";
import { deviceBus, type ShellMode } from "../os/device-host";
import type { ModuleManifest } from "@arclog/module-sdk";

type HomeIcon = { id: string; appId: string };

function loadHome(): HomeIcon[] {
  try {
    const raw = localStorage.getItem("arclog.homeIcons");
    if (!raw) return []; // empty home default (Android-like)
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed.filter((x) => x && x.appId);
  } catch {
    /* */
  }
  return [];
}

function saveHome(icons: HomeIcon[]) {
  try {
    localStorage.setItem("arclog.homeIcons", JSON.stringify(icons));
  } catch {
    /* */
  }
}

export function HomeGrid({ visible }: { visible: boolean }) {
  const store = osStore;
  const [, bump] = useState(0);
  const [icons, setIcons] = useState<HomeIcon[]>(loadHome);
  const [mode, setMode] = useState<ShellMode>(deviceBus.get().mode);
  const [dragId, setDragId] = useState<string | null>(null);

  useEffect(() => store.subscribe(() => bump((n) => n + 1)), [store]);
  useEffect(() => deviceBus.subscribe((s) => setMode(s.mode)), []);

  const byId = useMemo(() => {
    const m = new Map<string, ModuleManifest>();
    for (const p of store.packages) {
      if (p.state === "enabled" && p.manifest.kind === "app") {
        m.set(p.manifest.id || p.id, p.manifest);
      }
    }
    return m;
  }, [store.packages, bump]);

  if (!visible) return null;

  const cols = mode === "phone" ? 4 : mode === "tablet" ? 6 : 8;

  const open = (appId: string) => store.openApp(appId);

  const onDrop = (targetId: string | null) => {
    if (!dragId) return;
    setIcons((prev) => {
      const next = prev.slice();
      const from = next.findIndex((i) => i.id === dragId);
      if (from < 0) return prev;
      const [item] = next.splice(from, 1);
      if (!targetId) next.push(item);
      else {
        const to = next.findIndex((i) => i.id === targetId);
        next.splice(to < 0 ? next.length : to, 0, item);
      }
      saveHome(next);
      return next;
    });
    setDragId(null);
  };

  const addFromCatalog = (appId: string) => {
    setIcons((prev) => {
      if (prev.some((i) => i.appId === appId)) return prev;
      const next = [...prev, { id: crypto.randomUUID(), appId }];
      saveHome(next);
      return next;
    });
  };

  // expose simple API for context menus via window for now
  (window as any).__arclogHomeAdd = addFromCatalog;

  const items = icons
    .map((ic) => ({ ic, man: byId.get(ic.appId) }))
    .filter((x) => x.man);

  return (
    <div
      className={"home-grid mode-" + mode}
      style={{ ["--home-cols" as string]: String(cols) }}
      onDragOver={(e) => e.preventDefault()}
      onDrop={() => onDrop(null)}
    >
      {items.length === 0 && (
        <div className="home-empty">
          <div className="home-empty-title">Home</div>
          <div className="home-empty-sub">
            No icons on the home screen — that’s fine.
            <br />
            Open <strong>Applications</strong>, Spotlight, or App Manager to launch anything.
          </div>
          <button
            type="button"
            className="home-empty-btn"
            onClick={() => {
              sessionStorage.setItem("arclog.files.open", "/System/Applications");
              store.openApp("app.files", "Applications");
            }}
          >
            Browse Applications
          </button>
        </div>
      )}
      <div className="home-icons">
        {items.map(({ ic, man }) => {
          const Icon = resolveAppIcon(man!.id, man!.dock?.label || man!.name, man!.dock?.icon);
          return (
            <button
              key={ic.id}
              type="button"
              className={"home-icon" + (dragId === ic.id ? " dragging" : "")}
              draggable
              onDragStart={(e: DragEvent) => {
                setDragId(ic.id);
                e.dataTransfer.setData("text/plain", ic.id);
              }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onDrop(ic.id);
              }}
              onDoubleClick={() => open(man!.id)}
              onClick={() => {
                // mobile: single tap opens
                if (deviceBus.get().pointer === "coarse" || deviceBus.get().mode === "phone") {
                  open(man!.id);
                }
              }}
              onContextMenu={(e) => {
                e.preventDefault();
                e.stopPropagation();
                // simple remove
                setIcons((prev) => {
                  const next = prev.filter((x) => x.id !== ic.id);
                  saveHome(next);
                  return next;
                });
                store.notify("Removed from Home", "info");
              }}
            >
              <span className="home-icon-glyph">
                <Icon size={mode === "phone" ? 28 : 32} strokeWidth={1.75} />
              </span>
              <span className="home-icon-label">{man!.dock?.label || man!.name}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Persist pin-to-home from dock context later */
export function addAppToHome(appId: string) {
  const cur = loadHome();
  if (cur.some((i) => i.appId === appId)) return;
  cur.push({ id: crypto.randomUUID(), appId });
  saveHome(cur);
}
